import React, { createContext, useContext, useState, useEffect } from 'react';
import pb, { login, register, logout, currentUser } from '../pb.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('unknown');

  useEffect(() => {
    try {
      if (pb.authStore.isValid) {
        setUser(currentUser());
      } else {
        setUser(null);
      }
    } catch (initializationError) {
      console.error('Auth initialization error:', initializationError);
      setError('Failed to initialize authentication');
    } finally {
      setLoading(false);
    }

    const unsubscribe = pb.authStore.onChange((token, model) => {
      setUser(model || null);
      setError(null);
      setConnectionStatus(model ? 'connected' : 'unknown');
    });

    return unsubscribe;
  }, []);

  const signIn = async (email, password) => {
    try {
      setError(null);
      const authData = await login(email, password);
      setUser(authData.record);
      setConnectionStatus('connected');
      return authData;
    } catch (signInError) {
      const errorMessage = signInError.message || 'Failed to sign in';
      setError(errorMessage);
      setConnectionStatus('error');
      throw new Error(errorMessage);
    }
  };

  const signUp = async (email, password, passwordConfirm, name) => {
    try {
      setError(null);
      const authData = await register(email, password, passwordConfirm, name);
      setUser(authData.record);
      setConnectionStatus('connected');
      return authData;
    } catch (signUpError) {
      const errorMessage = signUpError.message || 'Failed to create account';
      setError(errorMessage);
      setConnectionStatus('error');
      throw new Error(errorMessage);
    }
  };

  const signOut = () => {
    logout();
    setUser(null);
    setError(null);
    setConnectionStatus('unknown');
  };

  const value = {
    user,
    loading,
    error,
    connectionStatus,
    signIn,
    signUp,
    signOut,
    isAuthenticated: !!user && pb.authStore.isValid
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};